import { memo, useMemo } from 'react';
import { Box, Text } from '@bonanza-social/moonlight';
import { useTranslation } from '@bonanza-social/i18n';
import StatusGemWithOutline from '../../StatusGemWithOutline/StatusGemWithOutline';
import { StatusPerkUpgradeLabelProps } from '../types';
import { formatPerkRewardAmount } from './StatusPerkUpgradeLabel.utils';

function StatusPerkUpgradeLabel({
  upgradeType,
  nextStatus,
  nextStatusPerkAmount,
  perkRewardType,
}: StatusPerkUpgradeLabelProps) {
  const [t] = useTranslation('status');

  const { textLeft, textRight } = useMemo((): {
    textLeft: string;
    textRight?: string;
  } => {
    switch (upgradeType) {
      case 'ReachNextStatusForMore':
        return {
          textLeft: t('perkLabelReach'),
          textRight: nextStatusPerkAmount
            ? t('perkLabelReachAmount', {
                amount: formatPerkRewardAmount(
                  nextStatusPerkAmount,
                  perkRewardType,
                ),
              })
            : undefined,
        };
      case 'ReachNextStatusToUpgrade':
        return {
          textLeft: t('perkLabelUpgradeWith'),
        };
      case 'KeepInNextStatus':
        return {
          textLeft: t('perkLabelUpgradeKeepIn'),
        };
      case 'ReachNextStatusToUnlock':
        return {
          textLeft: t('perkLabelReach'),
          textRight: t('perkLabelToUnlock'),
        };
      case 'MaxLevel':
      default:
        return {
          textLeft: t('perkLabelMaxLevel'),
        };
    }
  }, [upgradeType, t, nextStatusPerkAmount, perkRewardType]);

  return (
    <Box
      flexDirection="row"
      backgroundColor="primaryBackground"
      borderRadius="radius-full"
      alignItems="center"
      height={20}
      paddingLeft="space-200"
      paddingRight={
        textRight || upgradeType === 'MaxLevel' ? 'space-200' : 'space-0'
      }
    >
      <Text locNS="status" variant="badge" color="darkSurfacePrimary">
        {textLeft}
      </Text>
      {nextStatus ? (
        <Box position="relative" top="-space-025">
          <StatusGemWithOutline
            playerStatus={nextStatus}
            size="space-800"
            outlineColor="iconPrimary"
          />
        </Box>
      ) : null}
      {textRight ? (
        <Text locNS="status" variant="badge" color="darkSurfacePrimary">
          {textRight}
        </Text>
      ) : null}
    </Box>
  );
}

export default memo(StatusPerkUpgradeLabel);
